import { Link } from "@remix-run/react";

import { getBreadcrumbListJsonLd } from "~/utils/seo-meta";
import { SEO_ROUTE_GROUPS, type SeoRouteGroup } from "~/utils/seo-routes";

const BASE_URL = "https://prizo.io";

interface HubSeoPageProps {
  /** The hub group to render (its hub metadata + child routes). */
  group: SeoRouteGroup;
}

/**
 * Category index ("hub") page. Renders the H1 + intro, a grid of descriptive,
 * contextual links to every child page, and cross-links to the other hubs.
 * This is the spoke layer of the hub-and-spoke internal-linking architecture:
 * homepage → hub → leaf, with each link carrying topical anchor + description.
 */
export function HubSeoPage({ group }: HubSeoPageProps) {
  const { title, hubPath, hubH1, hubIntro, hubDescription, hubSections, routes } = group;
  const otherHubs = SEO_ROUTE_GROUPS.filter((g) => g.hubPath !== hubPath);

  // ItemList structured data — tells crawlers these child URLs belong to this hub.
  const itemListJsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: hubH1,
    description: hubDescription,
    url: `${BASE_URL}${hubPath}`,
    itemListElement: routes.map((route, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: route.label,
      url: `${BASE_URL}${route.path}`,
    })),
  };

  return (
    <div className="bg-[#ffffff] w-full">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            getBreadcrumbListJsonLd([
              { name: "Home", path: "/" },
              { name: title, path: hubPath },
            ]),
          ),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListJsonLd) }}
      />

      {/* Hero */}
      <div className="seo-section">
        {/* Breadcrumb trail (crawlable) */}
        <nav aria-label="Breadcrumb" className="mb-6 text-center">
          <ol className={`seo-font inline-flex items-center gap-2 text-[13px] text-[#6a6a6a]`}>
            <li>
              <Link to="/" className="hover:text-[#696cff] transition-colors">
                Home
              </Link>
            </li>
            <li aria-hidden="true">/</li>
            <li className="text-[#2c2c2c] font-medium">{title}</li>
          </ol>
        </nav>
        <div className="seo-hero-head">
          <h1 className="seo-h1">
            {hubH1}
          </h1>
          <p className="seo-lead">
            {hubIntro}
          </p>
        </div>
        <div className="seo-cta-row">
          <a
            href="https://apps.shopify.com/prizo"
            target="_blank"
            rel="noopener noreferrer"
            className="seo-btn-primary"
          >
            Try For Free
          </a>
          <a
            href="https://calendly.com/nadav-prizo/30min"
            target="_blank"
            rel="noopener noreferrer"
            className="seo-btn-secondary"
          >
            Book a Demo
          </a>
        </div>
      </div>

      {/* Child cards — descriptive, contextual internal links */}
      <div className="seo-band-grey">
        <div className="max-w-[1100px] mx-auto px-4 md:px-[60px]">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {routes.map((route) => (
              <Link
                key={route.path}
                to={route.path}
                className="group seo-card hover:shadow-md hover:border-[#696cff]/40 transition-all"
              >
                <h2 className={`seo-font font-semibold text-[#2c2c2c] text-[18px] mb-2 group-hover:text-[#696cff] transition-colors`}>
                  {route.label}
                </h2>
                {route.description && (
                  <p className={`seo-font text-[#6a6a6a] text-[14px] md:text-[15px] leading-[1.6]`}>
                    {route.description}
                  </p>
                )}
                <span className={`seo-font inline-block mt-4 text-[#696cff] text-[14px] font-medium`}>
                  Learn more →
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Long-form content — gives the hub real editorial depth for SEO */}
      {hubSections && hubSections.length > 0 && (
        <div className="max-w-[860px] mx-auto px-4 md:px-[60px] py-12 md:py-16">
          {hubSections.map((section, i) => (
            <section key={section.heading} className={i > 0 ? "mt-10 md:mt-12" : ""}>
              <h2 className={`seo-font font-semibold text-[#000000] text-[22px] md:text-[28px] mb-4`}>
                {section.heading}
              </h2>
              {section.paragraphs?.map((p, j) => (
                <p key={j} className={`seo-font text-[#4a4a4a] text-[16px] leading-[1.75] mb-4`}>
                  {p}
                </p>
              ))}
              {section.bullets && section.bullets.length > 0 && (
                <ul className="mt-2 space-y-3">
                  {section.bullets.map((b) => (
                    <li key={b.label} className={`seo-font text-[#4a4a4a] text-[16px] leading-[1.75]`}>
                      <span className="font-semibold text-[#2c2c2c]">{b.label}:</span> {b.text}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      )}

      {/* Cross-hub links — connect the four hubs to each other */}
      <div className="max-w-[1440px] mx-auto px-4 md:px-[120px] py-12 md:py-16">
        <h2 className={`seo-font font-semibold text-[#000000] text-[22px] md:text-[28px] text-center mb-8`}>
          Explore Prizo
        </h2>
        <div className="flex flex-wrap justify-center gap-4">
          {otherHubs.map((hub) => (
            <Link
              key={hub.hubPath}
              to={hub.hubPath}
              className={`seo-font px-5 py-3 bg-[#F6F6FF] rounded-2xl text-[#696cff] font-medium text-[14px] md:text-[16px] hover:bg-[#696cff] hover:text-white transition-colors`}
            >
              {hub.title}
            </Link>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="bg-[rgba(105,108,255,0.06)] w-full py-12 md:py-16">
        <div className="seo-wrap text-center">
          <p className={`seo-font text-[#6a6a6a] text-[16px] mb-6`}>
            Join brands that use Prizo to gamify checkout and boost revenue.
          </p>
          <a
            href="https://apps.shopify.com/prizo"
            target="_blank"
            rel="noopener noreferrer"
            className="seo-btn-cta"
          >
            Install on Shopify — Free Trial
          </a>
        </div>
      </div>
    </div>
  );
}
